import { FirebaseError } from "firebase/app";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import googleLogo from "../../shared/assets/img/googleLogo.svg";
import appleLogo from "../../shared/assets/img/appleLogo.svg";
import { Button } from "../../shared/components/Button";
import { ButtonsGroup } from "../../shared/components/ButtonsGroup";
import { ButtonTiny } from "../../shared/components/ButtonTiny";
import { OllieFloating } from "../../shared/components/OllieFloating";
import { SpeechBubble } from "../../shared/components/SpeechBubble";
import { useAuth } from "../../shared/hooks/Auth";
import { useAnalytics } from "../../shared/hooks/useAnalytics";
import { ROUTES } from "../../shared/routing/routes";
import { useLoggedInUserEdit } from '../../shared/hooks/useLoggedInUserEdit';
import { OnboardingProgress } from '../../types/obsessless-user';

export const RegisterScreen = () => {
    const navigate = useNavigate();
    const {
        linkAccountWithEmail,
        linkAccountWithGoogle,
        linkAccountWithApple,
        isAnonymous
    } = useAuth();
    const { logEvent } = useAnalytics();
    const { saveOnboardingProgress } = useLoggedInUserEdit();

    const [view, setView] = useState<"options" | "emailSignUp">("options");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newUserDisplayName, setNewUserDisplayName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        logEvent("register_screen_not_anonymous");
        if (!isAnonymous) {
            navigate(ROUTES.onboarding.highFive);
        }
    }, []);

    const getErrorMessage = (code: string) => {
        switch (code) {
            // Email/Password Registration Errors
            case "auth/email-already-in-use":
                return "An account with this email already exists. Try using a different email address.";
            case "auth/weak-password":
                return "Password should be at least 6 characters. Please choose a stronger password.";
            case "auth/invalid-email":
                return "Please enter a valid email address.";
            case "auth/operation-not-allowed":
                return "Email/password accounts are not enabled. Please contact support.";

            // OAuth/Social Authentication Errors
            case "auth/account-exists-with-different-credential":
                return "An account already exists. Try signing in with a different provider.";
            case "auth/popup-closed-by-user":
                return "Sign-in was cancelled. Please try again.";
            case "auth/popup-blocked":
                return "Pop-up was blocked by your browser. Please allow pop-ups and try again.";
            case "auth/cancelled-popup-request":
                return "Sign-in was cancelled. Please try again.";

            // Credential Linking Errors
            case "auth/provider-already-linked":
                return "This account is already linked with this provider.";
            case "auth/credential-already-in-use":
                return "This credential is already associated with a different account.";

            // Network and Connection Errors
            case "auth/network-request-failed":
                return "Network connection failed. Please check your internet connection and try again.";
            case "auth/timeout":
                return "The request timed out. Please try again.";

            // Rate Limiting
            case "auth/too-many-requests":
                return "Too many unsuccessful attempts. Please try again later.";

            // Configuration Errors
            case "auth/invalid-api-key":
                return "There's a configuration issue. Please contact support.";
            case "auth/app-not-authorized":
                return "This app is not authorized. Please contact support.";

            // Generic Errors
            default:
                return "An unexpected error occurred. Please try again.";
        }
    };

    const handleSignUpWithEmail = async () => {
        logEvent("sign_up_with_email");
        setError("");
        try {
            setIsLoading(true);
            await linkAccountWithEmail(newUserDisplayName, email, password);
            await saveOnboardingProgress(OnboardingProgress.GaveNotificationPermissions);
            navigate(ROUTES.onboarding.highFive);
        } catch (err) {
            console.error(err);
            if ((err as FirebaseError).code) {
                setError(getErrorMessage((err as FirebaseError).code));
            } else {
                setError("An error occurred. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignUpWithApple = async () => {
        logEvent("link_account_with_apple");
        setError("");
        try {
            setIsLoading(true);
            await linkAccountWithApple();
            navigate(ROUTES.onboarding.highFive);
        } catch (err) {
            console.error(err);
            if ((err as FirebaseError).code) {
                setError(getErrorMessage((err as FirebaseError).code));
            } else {
                setError("An error occurred.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignUpWithGoogle = async () => {
        logEvent("link_account_with_google");
        setError("");
        try {
            setIsLoading(true);
            await linkAccountWithGoogle();
            navigate(ROUTES.onboarding.highFive);
        } catch (err) {
            console.error(err);
            if ((err as FirebaseError).code) {
                setError(getErrorMessage((err as FirebaseError).code));
            } else {
                setError("An error occurred.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-10 safe-pb-4 px-8 flex-1 overflow-hidden">
            <title>Create Account</title>

            <div className="flex-1 flex flex-col justify-center items-center overflow-hidden">
                <div className="flex flex-col items-center justify-end overflow-show h-[250px]">
                    <SpeechBubble
                        text={
                            "To not lose access to your Personalised Companion please Sign up"
                        }
                    />
                    <OllieFloating />
                </div>
            </div>

            <div className="flex flex-col items-center gap-4 flex-shrink-0">
                {view === "options" && (
                    <div className="w-full flex flex-col items-center gap-2">
                        <h1 className="font-bold text-[24px] text-center">
                            Save Your Progress
                        </h1>

                        {error && (
                            <p className="text-red-500 text-xs text-center">{error}</p>
                        )}

                        <ButtonsGroup animated={false}>
                            <Button
                                onClick={handleSignUpWithApple}
                                label="Continue with Apple"
                                className="w-full"
                                isLoading={isLoading}
                                icon={
                                    <img
                                        src={appleLogo}
                                        alt="Apple Logo"
                                        className="w-3.5 h-3.5 mr-2"
                                    />
                                }
                            />

                            <Button
                                onClick={handleSignUpWithGoogle}
                                label="Continue with Google"
                                className="w-full bg-osGrey-4 text-osBlack"
                                variant="gray"
                                icon={
                                    <img
                                        src={googleLogo}
                                        alt="Google Logo"
                                        className="w-3.5 h-3.5 mr-2"
                                    />
                                }
                                isLoading={isLoading}
                            />

                            <Button
                                variant="gray"
                                onClick={() => setView("emailSignUp")}
                                className="w-full bg-osGrey-4 text-osBlack"
                                icon=""
                                label="Sign Up with Email"
                            />
                        </ButtonsGroup>
                    </div>
                )}

                {view === "emailSignUp" && (
                    <div className="w-full flex flex-col items-center gap-4">
                        <div className="w-full flex items-center justify-between">
                            <ButtonTiny variant="back" onClick={() => setView("options")} />
                            <h1 className="font-bold text-[28px]">Create Account</h1>
                            <div className="w-8"></div>
                        </div>

                        <ButtonsGroup animated={false}>
                            <input
                                type="text"
                                placeholder="Your Name"
                                className="w-full border-2 rounded-[18px] p-4 border-osGrey-4 focus:border-osBlack outline-osBlack focus:outline-none"
                                onChange={(e) => setNewUserDisplayName(e.target.value)}
                                maxLength={300}
                                value={newUserDisplayName}
                            />
                            <input
                                type="email"
                                placeholder="Email Address"
                                className="w-full border-2 rounded-[18px] p-4 border-osGrey-4 focus:border-osBlack outline-osBlack focus:outline-none"
                                onChange={(e) => setEmail(e.target.value)}
                                maxLength={300}
                                value={email}
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                className="w-full border-2 rounded-[18px] p-4 border-osGrey-4 focus:border-osBlack outline-osBlack focus:outline-none"
                                onChange={(e) => setPassword(e.target.value)}
                                maxLength={300}
                                value={password}
                            />
                            {error && (
                                <p className="text-red-500 text-sm text-center">{error}</p>
                            )}
                            <Button
                                onClick={handleSignUpWithEmail}
                                label="Sign Up"
                                className="w-full"
                                isLoading={isLoading}
                                inactive={!newUserDisplayName || !email || !password}
                            />
                        </ButtonsGroup>
                    </div>
                )}
            </div>
        </div>
    );
};
